import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {PatientService} from '@core/http/patient.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {combineLatest, firstValueFrom, fromEvent} from 'rxjs';
import {TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter,} from '@shared';
import {formatDate, NgIf, NgOptimizedImage} from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRippleModule} from '@angular/material/core';
import {FeatherIconsComponent} from '@shared/components/feather-icons/feather-icons.component';
import {MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BreadcrumbComponent} from '@shared/components/breadcrumb/breadcrumb.component';
import {BusinessGroupService} from "@core/service/business-group.service";
import {PatientDataSource} from "@core/data-source/PatientDataSource";
import {GeneralFunctionsService} from "@core/service/generalFunctions.service";
import {Patient} from "@core/models/Patient";
import {DoDeleteComponent} from "@shared/components/do-delete/do-delete.component";
import {Router} from "@angular/router";
import {FormatMobilePipe} from "@core/pipes/format-mobile.pipe";
import {PatientDiagnosticsComponent} from "./patient-diagnostics/patient-diagnostics.component";
import {auto} from "@popperjs/core";
import {AuthManagementService} from "@core/service/auth-management.service";
import {TopWidgetsComponent} from "@shared/components/top-widgets/top-widgets.component";
import {PatientHealthEvent} from "@core/models/PatientHealthEvent";
import {
  HealthEventsByIdPatientComponent
} from "../patient-health-event-list/health-events-by-id-patient/health-events-by-id-patient.component";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {BranchGroupService} from "@core/service/branch-group.service";
import {PrescriptionService} from "@core/http/table-data-services/prescription.service";
import {PatientPinDialogComponent} from "@shared/components/patient-pin-dialog/patient-pin-dialog.component";
import {downloadPrescriptionPdf} from "@shared/utils/window.utils";
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {RepositorioPacienteResponse} from "@core/interfaces/repositorio-paciente";
import {MsgResponse} from "@core/models/MsgResponse";
import {environment} from "@enviroment/environment";
import Swal from "sweetalert2";

@Component({
  selector: 'app-patient-list',
  templateUrl: './patient-list.component.html',
  styleUrls: ['./patient-list.component.scss'],
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    FeatherIconsComponent,
    MatRippleModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    NgOptimizedImage,
    FormatMobilePipe,
    NgIf,
    TopWidgetsComponent
  ],
})

export class PatientListComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit {

  isUserDoctor: boolean = false;
  isUserAdmin: boolean = false;
  isUserParticular: boolean = false;
  readonly showHipFeatures = environment.showHipFeatures;

  displayedColumns = [
    'name',
    'bDate',
    'email',
    'mobile',
    'status',
    'antecedentes',
    'acciones',
  ];

  data?: PatientService;
  dataSource!: PatientDataSource;
  index?: number;
  idPaciente?: string;
  patient?: Patient;
  healthEvent?: PatientHealthEvent;
  businessGroupId: string | null = null;
  branchGroupId: string | null = null;
  hipLoadingPatientId: string | null = null;
  @ViewChild(MatPaginator, {static: true})
  paginator!: MatPaginator;
  @ViewChild(MatSort, {static: true})
  sort!: MatSort;
  @ViewChild('filter', {static: true}) filter?: ElementRef;
  @Input() control: string = 'self';

  constructor(
    public httpClient: HttpClient,
    public dialog: MatDialog,
    private router: Router,
    public patientService: PatientService,
    private authManagement: AuthManagementService,
    private businessGroupService: BusinessGroupService,
    private gfs: GeneralFunctionsService,
    private branchGroupService: BranchGroupService,
    private prescriptionService: PrescriptionService,
    private httpMsgHandler: HttpMesageHandlerService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.isUserDoctor = this.authManagement.isUserDoctor();
    this.isUserAdmin = this.authManagement.isUserAdmin();
    this.isUserParticular = this.authManagement.isUserParticular();
  }

  ngOnInit() {
    // ADMIN: escucha GEMP + SUCURSAL
    // DOCTOR: solo GEMP
    this.subs.sink = combineLatest([
      this.businessGroupService.businessGroupId$,
      this.branchGroupService.branchGroupId$
    ]).subscribe(([gempId, branchId]) => {
      this.businessGroupId = gempId;

      if (this.isUserAdmin) {
        // Admin puede filtrar por sucursal
        this.branchGroupId = branchId;
        this.loadData();
      } else if (this.isUserDoctor || this.isUserParticular) {
        // Medico siempre por GEMP
        this.branchGroupId = null;
        this.loadData();
      }
    });
  }

  refresh() {
    this.ngOnInit();
  }

  addNew() {
    this.router.navigate(['patient/create-edit']).then();
  }

  editCall(row: Patient) {
    const idUsuario = btoa(row.idUsuario);
    this.router.navigate(['patient/create-edit/' + idUsuario]).then();
  }

  goToProfile(row: Patient) {
    const idUsuario = btoa(row.idUsuario);
    this.router.navigate(['patient/profile/' + idUsuario]).then();
  }

  showDiagnostics(row: Patient) {
    this.dialog.open(PatientDiagnosticsComponent, {
      width: '800px',
      height: auto,
      data: {
        diagnostics: row,
      },
    });
  }

  deleteItem(row: Patient) {
    this.idPaciente = row.idPaciente;

    const dialogRef = this.dialog.open(DoDeleteComponent, {
      data: {
        toDelete: 'Paciente',
        id: this.idPaciente,
        name: row.nombres,
        actionService: (id: string) => this.patientService.deletePatient(id),
      }
    });
    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.data?.dataChange.value.findIndex(
          (x): boolean => x.idPaciente === this.idPaciente
        );
        if (foundIndex != null && this.data) {
          this.data.dataChange.value.splice(foundIndex, 1);

          this.refreshTable();
        }
      }
    });
  }

  exportExcel() {
    const exportData: Partial<TableElement>[] =
      this.dataSource.filteredData.map((x: Patient) => ({
        Nombre: x.nombres,
        Genero: x.genero,
        Edad: x.edad,
        'Fecha de nacimiento': formatDate(new Date(x.fechaNacimiento), 'yyyy-MM-dd', 'en') || '',
        Correo: x.email,
        Telefono: x.movil,
        Estatus: x.status
      }));
    TableExportUtil.exportToExcel(exportData, 'excel');
  }

  public loadData() {
    this.data = new PatientService(this.httpClient, this.gfs, this.httpMsgHandler);
    this.dataSource = new PatientDataSource(
      this.businessGroupId,
      this.branchGroupId,
      this.data,
      this.paginator,
      this.sort
    );
    this.subs.sink = fromEvent(this.filter?.nativeElement, 'keyup').subscribe(
      () => {
        if (!this.dataSource) {
          return;
        }
        this.dataSource.filter = this.filter?.nativeElement.value;
      }
    );
    this.cdr.detectChanges();
  }

  goToRecetar(row: Patient) {
    this.router.navigate(['/prescription/create'], {state: {row}}).then();
  }

  viewHealthEvents(row: Patient) {
    this.dialog.open(HealthEventsByIdPatientComponent, {
      width: '70vw',
      maxWidth: 'none',
      height: 'fit-content',
      maxHeight: 'none',
      data: {
        title: 'Lista de Eventos de Salud',
        idPaciente: row.idPaciente
      }
    });
  }

  async downloadHip(row: Patient): Promise<void> {
    const idPaciente = (row?.idPaciente ?? '').toString().trim();
    if (!idPaciente || this.hipLoadingPatientId) return;

    this.hipLoadingPatientId = idPaciente;

    try {
      const response = await firstValueFrom(
        this.prescriptionService.getRepositorioPaciente(idPaciente, null)
      );

      if (this.isErrorOrWarning(response)) {
        this.httpMsgHandler.handleMsg(response as unknown as MsgResponse);
        return;
      }

      const repo = response?.data;
      let pin: string | null | undefined = null;

      if (repo?.hasHidden) {
        pin = await this.requestHipPin(repo);
        if (typeof pin === 'undefined') return;
      }

      const html = await firstValueFrom(
        this.prescriptionService.getRepositorioPacienteHtml(idPaciente, pin ?? null)
      );

      if (!this.downloadHipPdfOrShowError(html, row)) {
        return;
      }
    } catch (err) {
      this.httpMsgHandler.handleMsg(err as HttpErrorResponse);
    } finally {
      this.hipLoadingPatientId = null;
    }
  }

  private async requestHipPin(repo: RepositorioPacienteResponse): Promise<string | null | undefined> {
    const hasVisibleMeds = (repo?.tratamientoActivo?.length ?? 0) > 0;
    const mode = this.isUserAdmin
      ? 'ADMIN'
      : this.isUserDoctor || this.isUserParticular
        ? 'MEDICO'
        : 'PACIENTE';

    const dialogRef = this.dialog.open(PatientPinDialogComponent, {
      disableClose: false,
      autoFocus: false,
      width: '560px',
      maxWidth: '92vw',
      panelClass: ['rmd-pin-dialog-panel'],
      data: {
        mode,
        title: 'HIP CON MEDICAMENTOS OCULTOS',
        message: hasVisibleMeds
          ? 'EL PACIENTE TIENE MEDICAMENTOS OCULTOS. INGRESA EL PIN PARA INCLUIRLOS EN LA HIP O CONTINUA SIN PIN.'
          : 'EL PACIENTE TIENE MEDICAMENTOS OCULTOS. INGRESA EL PIN PARA GENERAR LA HIP CON EL TRATAMIENTO ACTIVO.',
        optional: true,
      },
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private downloadHipPdfOrShowError(raw: string, row: Patient): boolean {
    const html = (raw ?? '').toString();

    if (!html.trim()) {
      Swal.fire('AVISO', 'NO SE RECIBIO HTML PARA LA HIP.', 'info').then();
      return false;
    }

    if (!this.looksLikeHtml(html)) {
      const parsed = this.tryParseResponse(html);

      if (parsed) {
        this.httpMsgHandler.handleMsg(parsed as unknown as MsgResponse);
        return false;
      }

      Swal.fire('AVISO', 'LA RESPUESTA DE HIP NO TIENE FORMATO HTML.', 'warning').then();
      return false;
    }

    void downloadPrescriptionPdf(
      html,
      `hip-${row.idPaciente}.pdf`,
      {
        title: 'Descargando HIP...',
        subtitle: 'Estamos generando tu PDF'
      }
    );

    return true;
  }

  private looksLikeHtml(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return normalized.includes('<html') ||
      normalized.includes('<!doctype html') ||
      normalized.includes('<head') ||
      normalized.includes('<body');
  }

  private tryParseResponse(value: string): DefaultResponse<unknown> | null {
    try {
      return JSON.parse(value) as DefaultResponse<unknown>;
    } catch {
      return null;
    }
  }

  private isErrorOrWarning(response: DefaultResponse<unknown> | null | undefined): boolean {
    const toast = (response?.toast ?? '').toString().toLowerCase();
    return toast === 'error' || toast === 'warning';
  }

  private refreshTable() {
    this.paginator._changePageSize(this.paginator.pageSize);
  }
}
